import {
  AccreditationsCTA,
  FloatingSection,
  Header,
  Intro,
  NewsWidget,
  Stats,
} from "@prmf/site-components";
import { routes, t } from "@prmf/site-router";
import { NavLink } from "@remix-run/react";

export default function Sustainability() {
  return (
    <div>
      <Header
        title={t(routes.sustainability, "title")}
        image={t(routes.sustainability, "image")}
      >
        <>
          <p>
            We have been developing our approach to sustainability for over 15
            years but formalised our direction with the implementation of our
            Sustainability Strategy in 2019.
          </p>
          <p>
            As an organisation, we have set environmental, social and governance
            (ESG) objectives to optimise our impact on society, the environment
            and climate through ambitious targets on CO2e reduction and
            responsible and ethical sourcing.
          </p>
          <p>
            We recognise that sustainability is not a standalone activity; it
            needs to be part of ‘business as usual’, where we make considered
            decisions with a holistic view.
          </p>
        </>
      </Header>
      <Intro title="Environmental champions">
        <p>
          It is important that all our people learn about the impact of climate
          change on our planet and how we, as an organisation, are committed to
          taking steps to optimise the impact we have on the environment and
          society. Therefore, all the team at PRM, from kitchen designers and
          the manufacturing team to surveyors, installers, our sales team,
          management team and directors are kept informed about climate change.
          This has resulted in a workplace of environmental champions.
        </p>
      </Intro>
      <div className="space-y-10 py-10">
        <FloatingSection
          reverse
          bgColor="white"
          image="/media/assets/sustainability-book.jpg"
          title={
            <>
              <h3 className="font-semibold">
                Introducing our Little Book of Sustainability
              </h3>
              <p>
                The book is given out to our clients and for them to pass on as
                necessary. It is designed to be visually appealing and spread
                the word about our sustainability in a factual but fun way. We
                wanted the messages to be memorable and to easily illustrate how
                dedicated we are to improve our footprint.
              </p>
              <p>
                As well as our clients, it was vital that our own workforce
                bought in to our sustainability vision. We decided to launch the
                book internally in an interactive and fun way – not just leave
                copies for staff to pick up, but to ensure they would buy into
                our message. We decided to set up a Scavenger Hunt around the
                factory.
              </p>
              <p>
                <NavLink className="underline" to={routes.sustainabilityBook}>
                  Take a look inside and order your copy
                </NavLink>
              </p>
            </>
          }
        />
        <FloatingSection
          image="/media/assets/sustainability-environment.jpg"
          title={
            <>
              <h3 className="font-semibold">The environment</h3>
              <p>
                Paula Rosa Manhattan has a well-established,
                ISO14001:2015-registered, environmental management system and is
                also a founding member of the Furniture Industry Sustainability
                Programme (FISP).
              </p>
              <p>
                Through strategic policy and the setting of ambitious targets,
                we are significantly reducing our environmental impact.
                Infrastructure investment and a green-energy procurement
                strategy has enabled our production facility to be carbon
                neutral since 2020.
              </p>
              <p>
                In 2024 we will be joining the Science-Based Targets Initiative
                (SBTI) and have set reduction targets in line with the climate
                science.
              </p>
              <ul className="list-inside list-disc space-y-1">
                <li>
                  We help our customers create sustainable homes, communities
                  and reduce their carbon emissions.
                </li>
                <li>
                  We comply with the current identification of future
                  legislative requirements and have systems in place to do so.
                </li>
                <li>
                  We continually improve, assess and manage environmental risk.
                </li>
                <li>
                  We ensure the efficient use of raw materials and the ongoing
                  reduction of waste.
                </li>
              </ul>
            </>
          }
        />
        <FloatingSection
          reverse
          bgColor="white"
          image="/media/assets/sustainability-energy.jpg"
          title={
            <>
              <h3 className="font-semibold">Energy and Waste</h3>
              <p>
                We believe in zero manufacturing waste to landfill. None of the
                waste generated during our manufacturing process goes to
                landfill: this has been the case since 2012.
              </p>
              <p>
                Working in conjunction with our local partners, 80% of our
                manufacturing waste material is used in the generation of
                energy. In 2023 our process waste generated 450mwh of energy,
                enough to power 165 homes per year.
              </p>
            </>
          }
        />
        <FloatingSection
          reverse
          bgColor="white"
          image="/media/assets/sustainability-energy-2.jpg"
          title={
            <>
              <p>
                We have engineered the transport packaging on our products to a
                level that ensures the items remain safe in transit and while
                awaiting installation, but eliminates unnecessary waste. All our
                packaging is readily recyclable.
              </p>
              <p>
                Improving energy efficiency has been essential for all, not just
                from a cost perspective but also ensuring energy is used
                responsibly. We consumed 26% less electricity in 2023, while
                continuing to operate at 2022 levels.
              </p>
              <div className="mt-2">
                <p className="bg-accent inline">
                  We have demonstrated a 35% reduction in Scope 1 greenhouse gas
                  emissions per unit of production 2022–2023.
                </p>
              </div>
            </>
          }
        />
        <FloatingSection
          title={
            <>
              <h3 className="font-semibold">Future Homes</h3>
              <p>
                In 2025, compliance with the Future Homes Standard (FHS) will
                become mandatory. Its aim is to ensure that new homes built from
                2025 will produce 75–80% fewer carbon emissions than homes built
                under the current Building Regulations.
              </p>
              <p>
                Here at PRM we support the FHS and believe that the development
                of our products should always champion sustainability at the
                forefront of any decision making.
              </p>
              <p>
                Our strategic procurement strategy and policies drive us to
                innovate and make sustainable choices. We believe that
                sustainability should come as standard. We aim to offer
                specifiers an informed choice across selected products with
                regard to embodied and whole-life cycle carbon within 2024 and
                across our entire offering within two years.
              </p>
            </>
          }
          image="/media/assets/sustainability-future-homes.jpg"
        />
        <FloatingSection
          reverse
          bgColor="white"
          image="/media/assets/sustainability-sourcing.jpg"
          title={
            <>
              <h3 className="font-semibold">Responsible sourcing</h3>
              <p>
                As a producer of forest-based products, we are aware of our
                responsibility to ensure that our raw materials and kitchen
                components are ethically sourced. Our FSC® Chain of Custody
                Certification (FSC-C041323) demonstrates our commitment to the
                procurement of responsibly sourced timber.
              </p>
              <div className="mt-2">
                <p className="bg-accent inline">
                  All our processed parts are manufactured using renewable
                  ‘green’ energy and delivered via our own fleet of vehicles now
                  fuelled by HVO, a ‘fossil-free’ diesel alternative.
                </p>
              </div>
            </>
          }
        />
        <FloatingSection
          title={
            <>
              <h3 className="font-semibold">Group Sustainability Report</h3>
              <p>
                Sustainable development has been on the international agenda for
                almost 40 years, yet the global situation today is far from the
                most sustainable.
              </p>
              <p>
                This is Ballingslöv International’s fifth sustainability report
                and covers all the group’s companies. We champion high ethical
                standards and strive for our products to be developed, made,
                transported, used, reused and, finally, recycled with as little
                negative environmental impact as possible and the operations are
                based on long-term, sustainable solutions.
              </p>
              <a
                className="button mx-0 w-max border-2 border-black bg-transparent text-black"
                target="_blank"
                href="/media/pdf/ballingslov-group-sustainability-report.pdf"
              >
                View report
              </a>
            </>
          }
          image="/media/assets/sustainability-group-report.webp"
        />
      </div>
      <Stats
        className="md:gap-40"
        data={[
          {
            label: "Green energy used",
            value: "12.7",
            unit: "Gwh",
            since: "2018",
            bgColor: "#6AE8A4",
          },
          {
            label: "CO2 avoided",
            value: "2611",
            unit: "tonnes",
            since: "2018",
            bgColor: "#6AE8A4",
          },
        ]}
      />
      <AccreditationsCTA />
      <NewsWidget />
    </div>
  );
}
